// cache-bust-002-2026-06-12-1853
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { Loader2, LogOut, RefreshCw, Plus, X, Clock, FileDown, ShieldCheck, AlertTriangle, RotateCcw, CheckCircle2, Save, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import TrainFluoBadge from '../components/TrainFluoBadge';
import { isFluoBranded } from '../lib/train';
import { isGrandEstStation } from '../lib/grandEst';
import { getComposition } from '../data/mock';
import { searchStations } from '../api/client';
import { Link } from 'react-router-dom';
import LegalEditor from '../components/LegalEditor';
import AccessTab from '../components/AccessTab';
import ApiTab from '../components/admin/ApiTab';

const API = process.env.REACT_APP_BACKEND_URL;

/* ============ Small autocomplete used to add a station to the matrix ============ */
const StationAutocomplete = ({ onPick, onCancel }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { ref.current?.focus(); }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setBusy(true);
    const id = setTimeout(async () => {
      try {
        const r = await searchStations(q, 8);
        if (!cancelled) setResults(r || []);
      } catch { if (!cancelled) setResults([]); }
      finally { if (!cancelled) setBusy(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(id); };
  }, [q]);

  return (
    <div className="relative">
      <input
        ref={ref}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Tapez le nom d'une gare…"
        data-testid="admin-station-picker"
        className="h-9 px-3 w-72 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm"
      />
      {busy && <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-slate-400" />}
      {results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-72 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
          {results.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onPick({ id: s.id, name: s.name })}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
              >
                <div className="font-semibold text-slate-900">{s.name}</div>
                {s.region && <div className="text-[11px] text-slate-500">{s.region}</div>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button onClick={onCancel} className="ml-2 text-xs text-slate-500 underline">Annuler</button>
    </div>
  );
};

/* ============ Login screen ============ */
const LoginScreen = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setBusy(true); setError(null);
    try { await onLogin(username, password); }
    catch (err) { setError({ msg: err.message || 'Identifiants incorrects', status: err.status || 0 }); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-xl p-6">
        <div className="flex items-center gap-2 text-ter-blue font-extrabold text-lg mb-1">
          <ShieldCheck className="w-5 h-5" /> Espace admin Fluo Grand Est
        </div>
        <p className="text-xs text-slate-500 mb-4">Accès réservé. Veuillez vous identifier.</p>
        <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">Nom d'utilisateur</label>
        <input
          type="text"
          autoFocus
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          data-testid="admin-login-username"
          className="w-full h-10 px-3 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm"
        />
        <label className="block text-[11px] font-bold tracking-wider text-slate-500 mt-3 mb-1">Mot de passe</label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            data-testid="admin-login-password"
            className="w-full h-10 px-3 pr-9 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm font-mono"
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {error && (
          <div className={`mt-2 text-[12px] rounded px-2 py-1.5 flex items-center gap-1.5 ${
            error.status === 429
              ? 'text-amber-800 bg-amber-50 border border-amber-300'
              : 'text-red-700 bg-red-50 border border-red-200'
          }`}>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {error.msg}
          </div>
        )}
        <Button type="submit" disabled={busy || !username || !password} data-testid="admin-login-submit" className="mt-4 w-full bg-ter-blue hover:bg-ter-blue-dark text-white">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Se connecter
        </Button>
      </form>
    </div>
  );
};

/* ============ Status pill ============ */
const StatusPill = ({ status, delay }) => {
  if (!status) return null;
  const map = {
    on_time:  { bg: 'bg-emerald-100 text-emerald-800', label: 'À l\'heure' },
    delayed:  { bg: 'bg-amber-100 text-amber-900',     label: `+${delay} min` },
    cancelled:{ bg: 'bg-red-100 text-red-800',         label: 'Supprimé' },
  };
  const cfg = map[status] || map.on_time;
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${cfg.bg}`}>{cfg.label}</span>;
};

/* ============ Train row badge ============ */
const TrainBadge = ({ tr, anchorStation }) => {
  if (!isFluoBranded(tr.number)) return <TrainFluoBadge showLogo={false} />;
  const inGE = isGrandEstStation(tr.destination) || isGrandEstStation(anchorStation);
  return inGE ? <TrainFluoBadge /> : <TrainFluoBadge showLogo={false} label="Train régional" />;
};

/* ============ PDF export — landscape, readable, transposed ============ */
const exportAxisPdf = async ({ stations, rows, fromTime, windowMin }) => {
  try {
    const { default: jsPDF } = await import('jspdf');
    const autoTableMod = await import('jspdf-autotable');
    const autoTable = autoTableMod.default || autoTableMod;

    // Preload SNCF + Fluo logos for the footer band.
    const loadImage = (src) => new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataUrl: c.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
    const [sncfImg, fluoImg] = await Promise.all([
      loadImage('/assets/sncf_voyageurs.png'),
      loadImage('/assets/fluo_grand_est.png'),
    ]);

    // ---- We chunk the train columns across several A4 landscape pages so
    //      every cell stays wide enough to read. Empirically, ~14 trains
    //      per page = ~46 pt per column + 100 pt for the station label.
    const TRAINS_PER_PAGE = 14;
    const chunks = [];
    for (let i = 0; i < rows.length; i += TRAINS_PER_PAGE) {
      chunks.push(rows.slice(i, i + TRAINS_PER_PAGE));
    }
    if (!chunks.length) chunks.push([]);

    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const drawHeader = (pageIdx, total) => {
      doc.setFillColor(25, 25, 152); doc.rect(0, 0, pageW, 60, 'F');
      doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
      doc.text('Fluo Grand Est', 40, 28);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      doc.text('par SNCF Voyageurs - Vue ligne admin', 40, 44);
      doc.setFillColor(255, 205, 0); doc.rect(0, 60, pageW, 4, 'F');
      doc.setTextColor(25, 25, 152); doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
      doc.text(`${stations.length} gare(s) - ${rows.length} train(s)`, 40, 84);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
      const stamp = new Date().toLocaleString('fr-FR');
      doc.text(`Édité le ${stamp} - Fenêtre ${Math.round(windowMin / 60 * 10) / 10} h - À partir de ${fromTime || 'maintenant'}`, 40, 98);
      // page badge top right
      if (total > 1) {
        doc.setFontSize(8); doc.setTextColor(148, 163, 184);
        doc.text(`Page ${pageIdx + 1} / ${total}`, pageW - 70, 98);
      }
    };

    const drawFooter = () => {
      const top = pageH - 70;
      doc.setFillColor(255, 205, 0); doc.rect(0, top, pageW, 2, 'F');
      const logoH = 22; const logoY = pageH - 50;
      let x = 30;
      if (sncfImg) {
        const w = (sncfImg.w / sncfImg.h) * logoH;
        doc.addImage(sncfImg.dataUrl, 'PNG', x, logoY, w, logoH);
        x += w + 10;
      }
      if (fluoImg) {
        const w = (fluoImg.w / fluoImg.h) * logoH;
        doc.addImage(fluoImg.dataUrl, 'PNG', x, logoY, w, logoH);
        x += w + 10;
      }
      const tx = x + 6;
      const tw = pageW - tx - 30;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(100, 116, 139);
      doc.text(
        "Tableau dynamique des trains Fluo Grand Est par SNCF Voyageurs. Information voyageurs en temps réel via les données ouvertes SNCF (Navitia, SIRI, GTFS-RT) : horaires, voies, perturbations. - Usage interne admin.",
        tx, logoY + 6, { maxWidth: tw, lineHeightFactor: 1.25 }
      );
      doc.setFontSize(7); doc.setTextColor(148, 163, 184);
      doc.text("© 2026 SNCF Voyageurs - Fluo Grand Est. Tous droits réservés.", tx, pageH - 14, { maxWidth: tw });
    };

    // Pictograms for composition (drawn directly with jsPDF primitives so the
    // PDF stays self-contained — no extra image asset needed).
    const drawCompoCell = (data) => {
      if (data.section !== 'body' || data.column.index === 0 || data.row.index !== 0) return;
      const raw = data.cell.raw;
      if (!raw || typeof raw !== 'object' || !raw.__compo) return;
      const { wagons, seats } = raw.__compo;
      const { x, y, width, height } = data.cell;
      const cx = x + width / 2;
      // train pictogram (tiny carriage)
      const cy = y + 6;
      doc.setFillColor(25, 25, 152);
      const carW = 7, carH = 5, gap = 1.5;
      const maxCars = Math.min(wagons, 5);
      const totalW = maxCars * carW + (maxCars - 1) * gap;
      let startX = cx - totalW / 2;
      for (let i = 0; i < maxCars; i++) {
        doc.roundedRect(startX + i * (carW + gap), cy, carW, carH, 1, 1, 'F');
      }
      // overflow indicator
      if (wagons > maxCars) {
        doc.setFontSize(6); doc.setTextColor(25, 25, 152);
        doc.text(`+${wagons - maxCars}`, startX + totalW + 2, cy + carH);
      }
      // Numeric labels
      doc.setTextColor(25, 25, 152); doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
      doc.text(`${wagons} voit.`, cx, y + height - 9, { align: 'center' });
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5);
      doc.text(`${seats} pl.`, cx, y + height - 2, { align: 'center' });
    };

    chunks.forEach((chunkRows, chunkIdx) => {
      if (chunkIdx > 0) doc.addPage();
      drawHeader(chunkIdx, chunks.length);

      // --- Build the body for this chunk
      const head = [['Gare', ...chunkRows.map(r => r.number)]];

      // First body row = composition (rendered via didDrawCell pictograms)
      const isCancelled = (r) => Object.values(r.stops).some(s => s.status === 'cancelled');
      const compoRow = ['Composition', ...chunkRows.map(r => {
        if (isCancelled(r)) return { __compo: null, displayText: 'SUP' };
        const comp = getComposition(r.number);
        return comp ? { __compo: { wagons: comp.wagonsCount, seats: comp.totalSeats } } : '·';
      })];

      const stationRows = stations.map(s => {
        const cells = chunkRows.map(r => {
          const c = r.stops[s.id];
          if (!c) return '·';
          if (c.status === 'cancelled') return 'SUP';
          let txt = c.time;
          if (c.delay > 0) txt += `\n+${c.delay}`;
          if (c.platform && c.platform !== '—' && c.status !== 'cancelled') txt += `\nV${c.platform}`;
          return txt;
        });
        return [s.name, ...cells];
      });

      const destRow = ['Destination', ...chunkRows.map(r => r.destination || '·')];

      const body = [compoRow, ...stationRows, destRow];

      autoTable(doc, {
        startY: 112,
        head,
        body,
        styles: {
          font: 'helvetica',
          fontSize: 9,
          cellPadding: 5,
          textColor: [30, 41, 59],
          valign: 'middle',
          halign: 'center',
          lineWidth: 0.5,
          lineColor: [226, 232, 240],
        },
        headStyles: {
          fillColor: [25, 25, 152],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 10,
          cellPadding: { top: 6, right: 4, bottom: 6, left: 4 },
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { fontStyle: 'bold', halign: 'left', cellWidth: 95, fillColor: [241, 245, 249], textColor: [25, 25, 152] },
        },
        margin: { left: 30, right: 30, bottom: 80, top: 112 },
        didParseCell: (data) => {
          // First body row is composition (yellow background)
          if (data.section === 'body' && data.row.index === 0 && data.column.index > 0) {
            data.cell.styles.fillColor = [254, 240, 138]; // amber-200 tint
            data.cell.styles.minCellHeight = 30;
            if (typeof data.cell.raw === 'object' && data.cell.raw.__compo) {
              data.cell.text = ['']; // pictogram-rendered, no autotext
            }
          }
          // Destination = last body row (bold dark blue ribbon)
          if (data.section === 'body' && data.row.index === body.length - 1) {
            data.cell.styles.fillColor = [25, 25, 152];
            data.cell.styles.textColor = 255;
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 8;
          }
          // Cells: colours for delay / cancellation
          if (data.section === 'body' && data.row.index > 0 && data.row.index < body.length - 1 && data.column.index > 0) {
            const txt = String(data.cell.raw ?? '');
            if (txt === 'SUP') {
              data.cell.styles.textColor = [185, 28, 28];
              data.cell.styles.fontStyle = 'bold';
            } else if (txt.includes('+')) {
              data.cell.styles.textColor = [180, 83, 9];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
        didDrawCell: drawCompoCell,
      });

      drawFooter();
    });

    doc.save(`fluo-axe-admin-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.pdf`);
  } catch (err) {
    console.error('Admin PDF export failed:', err);
    alert("Erreur lors de l'export PDF : " + (err?.message || err));
  }
};

/* ============ Tab bar ============ */
const TABS = [
  { key: 'ligne',         label: 'Vue Ligne' },
  { key: 'quota',         label: 'Chiffres mobilité' },
  { key: 'communication', label: 'Communication' },
  { key: 'infofluo',     label: 'InfoFluo' },
  { key: 'legal',         label: 'Pages légales' },
  { key: 'maintenance',   label: 'Maintenance' },
  { key: 'access',        label: 'Accès' },
  { key: 'api',           label: 'API' },
];

const TabBar = ({ active, onChange, onLogout, permissions = {} }) => {
  const visibleTabs = TABS.filter(t => permissions[t.key] === true);
  return (
    <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center overflow-x-auto">
          <span className="flex items-center gap-1.5 text-ter-red text-[11px] font-bold uppercase tracking-wider mr-5 shrink-0 py-3.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Console admin
          </span>
          {visibleTabs.map(t => (
            <button
              key={t.key}
              onClick={() => onChange(t.key)}
              className={`px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                active === t.key
                  ? 'border-ter-blue text-ter-blue'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <Button onClick={onLogout} size="sm" variant="ghost" data-testid="admin-logout" className="text-slate-500 shrink-0 ml-4">
          <LogOut className="w-4 h-4 mr-1" /> Quitter
        </Button>
      </div>
    </div>
  );
};

/* ============ Tab — Chiffres mobilité ============ */
const QUOTA_FIELDS = [
  { key: 'trains', label: 'Trains / jour' },
  { key: 'cars',   label: 'Cars / jour' },
  { key: 'gares',  label: 'Gares' },
  { key: 'velos',  label: 'Vélos' },
];

const QuotaTab = ({ password }) => {
  const [fields, setFields] = useState({ trains: '', cars: '', gares: '', velos: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null); // { ok: bool, text: string }

  const load = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/quota`, { headers: { 'X-Admin-Password': password } });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      setFields(await r.json());
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setLoading(false); }
  }, [password]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/quota`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': password },
        body: JSON.stringify(fields),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      setMsg({ ok: true, text: 'Sauvegardé avec succès.' });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <h2 className="text-xl font-extrabold text-ter-blue mb-1">Chiffres mobilité</h2>
      <p className="text-sm text-slate-500 mb-6">
        Ces valeurs s'affichent dans la section "Fluo Grand Est, votre mobilité au quotidien !".
      </p>
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        {QUOTA_FIELDS.map(({ key, label }) => (
          <div key={key}>
            <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">{label}</label>
            <input
              type="text"
              value={fields[key] || ''}
              onChange={e => setFields(f => ({ ...f, [key]: e.target.value }))}
              className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm font-mono"
            />
          </div>
        ))}
        {msg && (
          <div className={`text-[12px] rounded px-3 py-2 flex items-center gap-1.5 ${
            msg.ok
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {msg.ok
              ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
            {msg.text}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button onClick={load} disabled={loading || saving} variant="outline" size="sm">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Charger
          </Button>
          <Button onClick={save} disabled={saving || loading} size="sm" className="bg-ter-blue hover:bg-ter-blue-dark text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Sauvegarder
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ============ ImagePicker ============ */
const ImagePicker = ({ value, onChange, password }) => {
  const [files, setFiles]         = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState(null);
  const fileRef                   = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/list_assets.php`)
      .then(r => r.json())
      .then(d => setFiles(d.files || []))
      .catch(() => {});
  }, []);

  const selectValue = (value || '').replace('/infofluo/assets/', '');

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${API}/api/upload_asset.php`, {
        method: 'POST',
        headers: { 'X-Admin-Password': password },
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erreur upload');
      setFiles(prev => [...new Set([...prev, d.filename])].sort());
      onChange(d.path);
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <select
          value={selectValue}
          onChange={e => onChange(e.target.value ? `/infofluo/assets/${e.target.value}` : '')}
          className="flex-1 h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue outline-none text-xs font-mono bg-white"
        >
          <option value="">— sélectionner —</option>
          {files.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="px-3 h-9 rounded-md border border-slate-300 text-xs text-slate-700 hover:bg-slate-50 shrink-0 flex items-center gap-1.5 disabled:opacity-50"
        >
          {uploading
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Plus className="w-3.5 h-3.5" />}
          Upload
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
      </div>
      {value && (
        <img
          src={value}
          alt=""
          className="h-16 rounded-md border border-slate-200 object-contain bg-slate-50"
          onError={e => { e.currentTarget.style.display = 'none'; }}
        />
      )}
      {error && <p className="text-[11px] text-red-600">{error}</p>}
    </div>
  );
};

/* ============ Tab — Communication ============ */
const COMM_LANGS = [
  { key: 'fr', label: 'FR — Français' },
  { key: 'en', label: 'EN — English' },
  { key: 'de', label: 'DE — Deutsch' },
  { key: 'lb', label: 'LB — Lëtzebuergesch' },
];

const CommunicationTab = ({ password }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [langues, setLangues]     = useState({ fr: {}, en: {}, de: {}, lb: {} });
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/communication`, {
        headers: { 'X-Admin-Password': password },
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      const d = await r.json();
      setCampaigns(d.campaigns || []);
      setLangues(d.langues   || { fr: {}, en: {}, de: {}, lb: {} });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setLoading(false); }
  }, [password]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/communication`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': password },
        body: JSON.stringify({ campaigns, langues }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      setMsg({ ok: true, text: 'Sauvegardé avec succès.' });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setSaving(false); }
  };

  const addCampaign = () => {
    const id = `campaign_${Date.now()}`;
    setCampaigns(prev => [...prev, {
      id,
      image:      '/infofluo/assets/',
      url:        '',
      target:     '_blank',
      icon:       'external-link',
      button_key: 'btn_ter',
      alt_key:    `${id}_alt`,
      title_key:  `${id}_title`,
    }]);
    setEditingId(id);
  };

  const removeCampaign = (id) => {
    setCampaigns(prev => prev.filter(c => c.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const updateCampaign = (id, field, value) =>
    setCampaigns(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));

  const updateLang = (lang, key, value) =>
    setLangues(prev => ({ ...prev, [lang]: { ...(prev[lang] || {}), [key]: value } }));

  const busy = loading || saving;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-extrabold text-ter-blue mb-1">Communication</h2>
          <p className="text-sm text-slate-500">
            Campagnes affichées dans l'encart latéral. Les textes alt, titre et bouton sont gérés par langue.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={load} disabled={busy} variant="outline" size="sm">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Charger
          </Button>
          <Button onClick={save} disabled={busy} size="sm" className="bg-ter-blue hover:bg-ter-blue-dark text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Sauvegarder tout
          </Button>
        </div>
      </div>

      {/* Feedback */}
      {msg && (
        <div className={`text-[12px] rounded px-3 py-2 flex items-center gap-1.5 mb-4 ${
          msg.ok
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {msg.ok
            ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
          {msg.text}
        </div>
      )}

      {/* Campaign cards */}
      <div className="space-y-3 mb-4">
        {campaigns.map(c => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">

            {/* Card header */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 shrink-0 overflow-hidden flex items-center justify-center">
                <img
                  src={c.image}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{langues['fr']?.[c.title_key] || c.id}</div>
                <div className="text-[11px] text-slate-400 font-mono truncate">{c.image}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => setEditingId(editingId === c.id ? null : c.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    editingId === c.id
                      ? 'bg-ter-blue text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {editingId === c.id ? 'Fermer' : 'Modifier'}
                </button>
                <button
                  onClick={() => removeCampaign(c.id)}
                  title="Supprimer cette campagne"
                  className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Expanded editor */}
            {editingId === c.id && (
              <div className="border-t border-slate-100 bg-slate-50 px-4 py-4 space-y-5">

                {/* Structural fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">Image</label>
                    <ImagePicker value={c.image} onChange={v => updateCampaign(c.id, 'image', v)} password={password} />
                  </div>
                  {[
                    { field: 'url',        label: 'URL destination',  placeholder: 'https://...' },
                    { field: 'icon',       label: 'Icône bouton',    placeholder: 'external-link' },
                    { field: 'button_key', label: 'Clé bouton',      placeholder: 'btn_ter' },
                  ].map(({ field, label, placeholder }) => (
                    <div key={field}>
                      <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">{label}</label>
                      <input
                        type="text"
                        value={c[field] || ''}
                        onChange={e => updateCampaign(c.id, field, e.target.value)}
                        placeholder={placeholder}
                        className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-xs font-mono bg-white"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">Target</label>
                    <select
                      value={c.target || '_blank'}
                      onChange={e => updateCampaign(c.id, 'target', e.target.value)}
                      className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue outline-none text-xs bg-white"
                    >
                      <option value="_blank">_blank — nouvel onglet</option>
                      <option value="_self">_self — même onglet</option>
                    </select>
                  </div>
                </div>

                {/* Language table */}
                <div>
                  <div className="text-[11px] font-bold tracking-wider text-slate-500 mb-2 uppercase">Textes par langue</div>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="px-3 py-2 text-left font-bold text-slate-500 w-36 border-b border-slate-200">Langue</th>
                          <th className="px-3 py-2 text-left font-bold text-slate-500 border-b border-slate-200">Alt</th>
                          <th className="px-3 py-2 text-left font-bold text-slate-500 border-b border-slate-200">Titre badge</th>
                          <th className="px-3 py-2 text-left font-bold text-slate-500 border-b border-slate-200">Bouton</th>
                        </tr>
                      </thead>
                      <tbody>
                        {COMM_LANGS.map(({ key: lang, label }) => (
                          <tr key={lang} className="border-b border-slate-100 last:border-0 odd:bg-white even:bg-slate-50/50">
                            <td className="px-3 py-2 font-bold text-ter-blue whitespace-nowrap">{label}</td>
                            {[c.alt_key, c.title_key, c.button_key].map(k => (
                              <td key={k} className="px-2 py-1.5">
                                <input
                                  type="text"
                                  value={langues[lang]?.[k] || ''}
                                  onChange={e => updateLang(lang, k, e.target.value)}
                                  className="w-full h-7 px-2 rounded border border-slate-200 focus:border-ter-blue outline-none bg-white text-xs"
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-1.5 text-[10px] text-slate-400 font-mono">
                    alt_key: {c.alt_key} · title_key: {c.title_key} · button_key: {c.button_key}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {loading && campaigns.length === 0 && (
          <div className="py-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        )}
      </div>

      <button
        onClick={addCampaign}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border-2 border-dashed border-slate-300 text-sm text-slate-500 hover:border-ter-blue hover:text-ter-blue transition-colors disabled:opacity-50"
      >
        <Plus className="w-4 h-4" /> Ajouter une campagne
      </button>
    </div>
  );
};

/* ============ Tab — InfoFluo ============ */
const INFO_LANGS = [
  { key: 'fr', label: 'FR — Français' },
  { key: 'en', label: 'EN — English' },
  { key: 'de', label: 'DE — Deutsch' },
  { key: 'lb', label: 'LB — Lëtzebuergesch' },
];

const INFO_THEMES = [
  { value: 'canal',  label: 'Canal — liens officiels' },
  { value: 'equipe', label: 'Équipe — contact' },
  { value: 'plan',   label: 'Plan — réseaux sociaux' },
];

const INFO_ICONS = [
  { value: 'atsign',    label: '@  — Site web' },
  { value: 'whatsapp',  label: 'W  — WhatsApp' },
  { value: 'facebook',  label: 'f  — Facebook' },
  { value: 'instagram', label: 'IG — Instagram' },
];

const INFO_ACCENTS = [
  { value: 'blue',      label: 'Bleu TER' },
  { value: 'whatsapp',  label: 'Vert (WhatsApp)' },
  { value: 'facebook',  label: 'Bleu (Facebook)' },
  { value: 'instagram', label: 'Dégradé Instagram' },
];

const THEME_META = {
  canal:  { label: 'Canal',  desc: 'liens officiels' },
  equipe: { label: 'Équipe', desc: 'contact' },
  plan:   { label: 'Plan',   desc: 'réseaux sociaux' },
};
const THEME_ORDER = ['canal', 'equipe', 'plan'];

const EntryCard = ({ entry, editingId, setEditingId, removeEntry, updateEntry, langues, updateLang, INFO_LANGS }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    {/* Card header */}
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center">
        <span className="text-[10px] font-bold text-slate-500 uppercase">{entry.icon?.slice(0, 2)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate">
          {langues['fr']?.[entry.title_key] || entry.id}
        </div>
        <div className="text-[11px] text-slate-400 font-mono truncate">{entry.url}</div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={() => setEditingId(editingId === entry.id ? null : entry.id)}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            editingId === entry.id
              ? 'bg-ter-blue text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          {editingId === entry.id ? 'Fermer' : 'Modifier'}
        </button>
        <button
          onClick={() => removeEntry(entry.id)}
          title="Supprimer cette entrée"
          className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>

    {/* Expanded editor */}
    {editingId === entry.id && (
      <div className="border-t border-slate-100 bg-slate-50 px-4 py-4 space-y-5">
        {/* Structural fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">Thème</label>
            <select
              value={entry.theme || 'canal'}
              onChange={e => updateEntry(entry.id, 'theme', e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue outline-none text-xs bg-white"
            >
              {INFO_THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">Icône</label>
            <select
              value={entry.icon || 'atsign'}
              onChange={e => updateEntry(entry.id, 'icon', e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue outline-none text-xs bg-white"
            >
              {INFO_ICONS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">Accent couleur</label>
            <select
              value={entry.accent || 'blue'}
              onChange={e => updateEntry(entry.id, 'accent', e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue outline-none text-xs bg-white"
            >
              {INFO_ACCENTS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">Target</label>
            <select
              value={entry.target || '_blank'}
              onChange={e => updateEntry(entry.id, 'target', e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue outline-none text-xs bg-white"
            >
              <option value="_blank">_blank — nouvel onglet</option>
              <option value="_self">_self — même onglet</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-bold tracking-wider text-slate-500 mb-1">URL</label>
            <input
              type="url"
              value={entry.url || ''}
              onChange={e => updateEntry(entry.id, 'url', e.target.value)}
              placeholder="https://..."
              className="w-full h-9 px-3 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-xs font-mono bg-white"
            />
          </div>
        </div>

        {/* Language table */}
        <div>
          <div className="text-[11px] font-bold tracking-wider text-slate-500 mb-2 uppercase">Textes par langue</div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-3 py-2 text-left font-bold text-slate-500 w-36 border-b border-slate-200">Langue</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-500 border-b border-slate-200">Titre</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-500 border-b border-slate-200">Sous-titre</th>
                </tr>
              </thead>
              <tbody>
                {INFO_LANGS.map(({ key: lang, label }) => (
                  <tr key={lang} className="border-b border-slate-100 last:border-0 odd:bg-white even:bg-slate-50/50">
                    <td className="px-3 py-2 font-bold text-ter-blue whitespace-nowrap">{label}</td>
                    {[entry.title_key, entry.subtitle_key].map(k => (
                      <td key={k} className="px-2 py-1.5">
                        <input
                          type="text"
                          value={langues[lang]?.[k] || ''}
                          onChange={e => updateLang(lang, k, e.target.value)}
                          className="w-full h-7 px-2 rounded border border-slate-200 focus:border-ter-blue outline-none bg-white text-xs"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-1.5 text-[10px] text-slate-400 font-mono">
            title_key: {entry.title_key} · subtitle_key: {entry.subtitle_key}
          </div>
        </div>
      </div>
    )}
  </div>
);

const InfoFluoTab = ({ password }) => {
  const [entries, setEntries] = useState([]);
  const [langues, setLangues] = useState({ fr: {}, en: {}, de: {}, lb: {} });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/info`, {
        headers: { 'X-Admin-Password': password },
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      const d = await r.json();
      setEntries(d.entries || []);
      setLangues(d.langues  || { fr: {}, en: {}, de: {}, lb: {} });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setLoading(false); }
  }, [password]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': password },
        body: JSON.stringify({ entries, langues }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur serveur');
      setMsg({ ok: true, text: 'Sauvegardé avec succès.' });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setSaving(false); }
  };

  const addEntry = () => {
    const id = `entry_${Date.now()}`;
    setEntries(prev => [...prev, {
      id,
      theme:        'canal',
      icon:         'atsign',
      accent:       'blue',
      title_key:    `${id}_label`,
      subtitle_key: `${id}_sublabel`,
      url:          '',
      target:       '_blank',
      testId:       `info-link-${id}`,
    }]);
    setEditingId(id);
  };

  const removeEntry = (id) => {
    setEntries(prev => prev.filter(e => e.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const updateEntry = (id, field, value) =>
    setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));

  const updateLang = (lang, key, value) =>
    setLangues(prev => ({ ...prev, [lang]: { ...(prev[lang] || {}), [key]: value } }));

  const busy = loading || saving;

  const knownThemes = new Set(THEME_ORDER);
  const ungrouped   = entries.filter(e => !knownThemes.has(e.theme));

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-extrabold text-ter-blue mb-1">InfoFluo</h2>
          <p className="text-sm text-slate-500">
            Liens et canaux officiels de la section "Toute l'info Fluo", groupés par thème.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={load} disabled={busy} variant="outline" size="sm">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Charger
          </Button>
          <Button onClick={save} disabled={busy} size="sm" className="bg-ter-blue hover:bg-ter-blue-dark text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Sauvegarder tout
          </Button>
        </div>
      </div>

      {/* Feedback */}
      {msg && (
        <div className={`text-[12px] rounded px-3 py-2 flex items-center gap-1.5 mb-4 ${
          msg.ok
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {msg.ok
            ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
          {msg.text}
        </div>
      )}

      {/* Grouped entry cards */}
      <div className="space-y-6 mb-4">
        {THEME_ORDER.map(theme => {
          const items = entries.filter(e => e.theme === theme);
          if (!items.length) return null;
          const meta = THEME_META[theme];
          return (
            <div key={theme}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">{meta.label}</span>
                <span className="text-[10px] text-slate-400">— {meta.desc}</span>
                <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-500">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map(entry => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  editingId={editingId}
                  setEditingId={setEditingId}
                  removeEntry={removeEntry}
                  updateEntry={updateEntry}
                  langues={langues}
                  updateLang={updateLang}
                  INFO_LANGS={INFO_LANGS}
                />
              ))}
              </div>
            </div>
          );
        })}

        {ungrouped.length > 0 && (
          <div>
            <div className="text-[11px] font-bold tracking-wider text-slate-400 uppercase mb-2">Autre</div>
            <div className="space-y-2">
              {ungrouped.map(entry => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  editingId={editingId}
                  setEditingId={setEditingId}
                  removeEntry={removeEntry}
                  updateEntry={updateEntry}
                  langues={langues}
                  updateLang={updateLang}
                  INFO_LANGS={INFO_LANGS}
                />
              ))}
            </div>
          </div>
        )}

        {loading && entries.length === 0 && (
          <div className="py-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        )}
      </div>

      <button
        onClick={addEntry}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border-2 border-dashed border-slate-300 text-sm text-slate-500 hover:border-ter-blue hover:text-ter-blue transition-colors disabled:opacity-50"
      >
        <Plus className="w-4 h-4" /> Ajouter une entrée
      </button>
    </div>
  );
};

/* ============ Main view ============ */
const STATIONS_LS_KEY = 'fluo-admin-stations-v1';

const loadSavedStations = () => {
  try {
    const raw = localStorage.getItem(STATIONS_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every(s => s && s.id && s.name)) return parsed;
    return null;
  } catch { return null; }
};

const AxisBoard = ({ password }) => {
  const [stations, setStations] = useState(() => loadSavedStations() || []);
  const [windowMin, setWindowMin] = useState(180);
  const [fromTime, setFromTime] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picker, setPicker] = useState(null);   // null | { station }
  const [showDetail, setShowDetail] = useState(null); // selected row

  // Bootstrap with backend defaults ONLY when no saved selection in localStorage.
  useEffect(() => {
    if (loadSavedStations()) return; // user already has a personalised list
    fetch(`${API}/api/admin/axis-defaults`, { headers: { 'X-Admin-Password': password } })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setStations(d.stations || []))
      .catch(() => setStations([]));
  }, [password]);

  // Persist every change so the next visit keeps the same axis.
  useEffect(() => {
    try { localStorage.setItem(STATIONS_LS_KEY, JSON.stringify(stations)); }
    catch { /* quota / privacy mode — ignore */ }
  }, [stations]);

  const resetToDefaults = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/axis-defaults`, { headers: { 'X-Admin-Password': password } });
      if (!r.ok) throw new Error('Réinitialisation impossible');
      const d = await r.json();
      setStations(d.stations || []);
    } catch (e) { setError(e.message); }
  }, [password]);

  const refresh = useCallback(async () => {
    if (!stations.length) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/admin/axis-board`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': password },
        body: JSON.stringify({ stations, from_time: fromTime || null, window_minutes: windowMin }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur serveur');
      setData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [stations, fromTime, windowMin, password]);

  // Auto-refresh whenever stations / window / from_time change
  useEffect(() => { refresh(); }, [refresh]);

  const removeStation = (id) => setStations(s => s.filter(x => x.id !== id));
  const addStation = (st) => setStations(s => s.some(x => x.id === st.id) ? s : [...s, st]);
  const moveStation = (idx, dir) => setStations(s => {
    const next = [...s]; const j = idx + dir;
    if (j < 0 || j >= next.length) return s;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const rows = data?.rows || [];
  const isPivNotice = !!data?.notice;

  return (
    <section className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
      <header className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-ter-blue">Vue ligne : axe configurable</h1>
          <p className="text-sm text-slate-500 mt-0.5">Tableau matriciel des trains sur les gares sélectionnées (fenêtre 1 h à 6 h).</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={refresh} disabled={loading} size="sm" variant="outline" data-testid="admin-refresh">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Actualiser
          </Button>
          <Button onClick={() => exportAxisPdf({ stations, rows, fromTime, windowMin })} size="sm" variant="outline" disabled={!rows.length} data-testid="admin-export-pdf">
            <FileDown className="w-4 h-4 mr-1" /> Export PDF
          </Button>
        </div>
      </header>

      {/* Settings row */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="flex items-center flex-wrap gap-x-6 gap-y-3 mb-3">
          <label className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600 font-semibold">À partir de</span>
            <input type="time" value={fromTime} onChange={e => setFromTime(e.target.value)}
              data-testid="admin-from-time"
              className="h-9 px-2 rounded-md border border-slate-300 text-sm font-mono" />
            {fromTime && <button onClick={() => setFromTime('')} className="text-xs text-slate-500 underline">maintenant</button>}
          </label>
          <label className="flex items-center gap-3 text-sm">
            <span className="text-slate-600 font-semibold">Fenêtre</span>
            <input type="range" min={60} max={360} step={30} value={windowMin}
              onChange={e => setWindowMin(parseInt(e.target.value, 10))}
              data-testid="admin-window-slider"
              className="w-48 accent-ter-blue" />
            <span className="font-bold text-ter-blue tabular-nums w-12 text-right">{Math.round(windowMin / 60 * 10) / 10}h</span>
          </label>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] tracking-wider text-slate-500 font-bold uppercase">Gares affichées ({stations.length})</div>
            <button
              onClick={resetToDefaults}
              data-testid="admin-reset-stations"
              title="Restaurer la liste par défaut (Nancy → Metz → Luxembourg)"
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-ter-blue font-semibold"
            >
              <RotateCcw className="w-3 h-3" /> Réinitialiser
            </button>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {stations.map((s, i) => (
              <span key={s.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs">
                <button title="Monter" disabled={i === 0} onClick={() => moveStation(i, -1)} className="text-slate-400 hover:text-ter-blue disabled:opacity-30">↑</button>
                <button title="Descendre" disabled={i === stations.length - 1} onClick={() => moveStation(i, +1)} className="text-slate-400 hover:text-ter-blue disabled:opacity-30">↓</button>
                <span className="font-semibold text-slate-700">{s.name}</span>
                <button onClick={() => removeStation(s.id)} className="text-slate-400 hover:text-red-600" title="Retirer"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {!picker && (
              <button onClick={() => setPicker({})} data-testid="admin-add-station-btn" className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-dashed border-slate-300 text-xs text-slate-600 hover:border-ter-blue hover:text-ter-blue">
                <Plus className="w-3 h-3" /> Ajouter une gare
              </button>
            )}
          </div>
          {picker && (
            <div className="mt-2 flex items-center gap-2">
              <StationAutocomplete
                onPick={(s) => { addStation(s); setPicker(null); }}
                onCancel={() => setPicker(null)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Matrix */}
      {error && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-800 mb-3">{error}</div>
      )}
      {data?.errors?.length > 0 && (
        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-[12px] text-amber-900 mb-3">
          ⚠ Données partielles : {data.errors.map(e => `${e.station} (${e.error})`).join(' · ')}
        </div>
      )}

      {isPivNotice ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center text-sm text-amber-900">
          Aucune donnée disponible — API PIV non encore implémentée
        </div>
      ) : (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm border-collapse" style={{ minWidth: '100%' }}>
            <thead>
              {/* Row 1: train numbers (with Fluo badge mini) */}
              <tr className="bg-ter-blue text-white">
                <th className="px-3 py-2.5 text-left sticky left-0 bg-ter-blue z-10 min-w-[180px] text-[11px] uppercase tracking-wider font-bold">
                  Numéro de circulation
                </th>
                {rows.map((r) => (
                  <th
                    key={r.number}
                    onClick={() => setShowDetail(r)}
                    title="Voir le détail"
                    className="px-2 py-1.5 text-center font-mono tabular-nums font-bold cursor-pointer hover:bg-ter-blue-dark min-w-[64px] whitespace-nowrap"
                  >
                    {r.number}
                  </th>
                ))}
              </tr>
              {/* Row 2: composition (nb voitures · places) — yellow band */}
              <tr className="bg-ter-yellow text-ter-blue">
                <th className="px-3 py-1.5 text-left sticky left-0 bg-ter-yellow z-10 text-[10px] uppercase tracking-wider font-bold">
                  Composition
                </th>
                {rows.map((r) => {
                  const isCancelled = Object.values(r.stops).some(s => s.status === 'cancelled');
                  const comp = isCancelled ? null : getComposition(r.number);
                  return (
                    <th key={r.number} className="px-1 py-1.5 text-center text-[10px] font-bold whitespace-nowrap">
                      {comp ? `${comp.wagonsCount}v · ${comp.totalSeats}pl` : '—'}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {stations.map((s, sIdx) => (
                <tr key={s.id} className={sIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-3 py-2 font-bold text-slate-800 sticky left-0 z-[1] whitespace-nowrap text-[12px]" style={{ backgroundColor: sIdx % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                    {s.name}
                  </td>
                  {rows.map((r) => {
                    const c = r.stops[s.id];
                    if (!c) return <td key={r.number} className="px-1 py-2 text-center text-slate-300 text-xs">·</td>;
                    const cancelled = c.status === 'cancelled';
                    return (
                      <td key={r.number} className="px-1 py-2 text-center whitespace-nowrap" title={c.platform && c.platform !== '—' ? `Voie ${c.platform}` : ''}>
                        <div className={`font-mono text-[12px] tabular-nums ${cancelled ? 'line-through text-red-700 font-bold' : c.delay > 0 ? 'text-amber-800 font-bold' : 'text-slate-900'}`}>
                          {c.time}
                        </div>
                        {c.delay > 0 && !cancelled && (
                          <div className="text-[9px] text-amber-700 font-bold leading-none">+{c.delay}</div>
                        )}
                        {c.platform && c.platform !== '—' && !cancelled && (
                          <div className="text-[9px] text-slate-500 leading-none mt-0.5">V{c.platform}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Destination row */}
              <tr className="bg-ter-blue text-white">
                <td className="px-3 py-2 font-bold sticky left-0 bg-ter-blue z-[1] text-[11px] uppercase tracking-wider">
                  Destination
                </td>
                {rows.map((r) => (
                  <td key={r.number} className="px-1 py-2 text-center text-[10px] font-bold whitespace-nowrap" title={r.destination}>
                    {(r.destination || '·').slice(0, 14)}
                  </td>
                ))}
              </tr>
              {!rows.length && !loading && (
                <tr><td colSpan={2} className="text-center py-8 text-slate-500 text-sm">Aucun train sur la fenêtre sélectionnée.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={2} className="text-center py-8 text-slate-500 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Chargement…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <p className="text-[11px] text-slate-400 mt-3">Lecture verticale : chaque colonne = un train, chaque ligne = une gare. Cliquez sur un numéro de train pour son détail complet.</p>

      {/* Detail modal */}
      {showDetail && (
        <DetailModal row={showDetail} stations={stations} onClose={() => setShowDetail(null)} />
      )}
    </section>
  );
};

/* ============ Detail modal ============ */
const DetailModal = ({ row, stations, onClose }) => {
  const comp = row.stops && !Object.values(row.stops).some(s => s.status === 'cancelled') ? getComposition(row.number) : null;
  return (
    <div className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center bg-black/40 p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-ter-blue text-white px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 font-extrabold">
            <TrainFluoBadge showLogo={isFluoBranded(row.number)} />
            <span>{row.number}</span>
            {row.destination && <span className="font-normal text-white/80 text-sm">→ {row.destination}</span>}
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          {comp && (
            <div className="text-[12px] text-slate-700 mb-3">
              Composition : <strong>{comp.wagonsCount}</strong> voitures · <strong>{comp.totalSeats}</strong> places · <strong>{comp.label}</strong>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-1.5">Gare</th>
                <th className="text-left py-1.5">Heure</th>
                <th className="text-left py-1.5">Voie</th>
                <th className="text-left py-1.5">Statut</th>
              </tr>
            </thead>
            <tbody>
              {stations.map(s => {
                const c = row.stops[s.id];
                if (!c) return null;
                return (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="py-1.5 font-semibold">{s.name}</td>
                    <td className="py-1.5 font-mono tabular-nums">
                      {c.delay > 0 ? <><span className="line-through text-slate-400">{c.time}</span> <strong className="text-amber-700">{c.realTime}</strong></> : c.time}
                    </td>
                    <td className="py-1.5">{c.platform && c.platform !== '—' ? <span className="text-[10px] px-1 rounded bg-slate-100 font-bold">V{c.platform}</span> : <span className="text-slate-300">—</span>}</td>
                    <td className="py-1.5"><StatusPill status={c.status} delay={c.delay} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {row.vehicle_journey_id && (
            <Link to={`/train/${encodeURIComponent(row.vehicle_journey_id)}?number=${encodeURIComponent(row.number)}`}
              className="mt-4 inline-block text-sm text-ter-blue font-semibold hover:underline">
              Voir l'itinéraire complet →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

/* ============ Maintenance tab ============ */
const MAINT_LANGS = [
  { key: 'fr', flag: '🇫🇷', label: 'Français' },
  { key: 'en', flag: '🇬🇧', label: 'English' },
  { key: 'de', flag: '🇩🇪', label: 'Deutsch' },
  { key: 'lb', flag: '🇱🇺', label: 'Lëtzebuergesch' },
];

const DEFAULT_MAINT = {
  enabled: false,
  message: { fr: '', en: '', de: '', lb: '' },
  button:  { fr: 'OK, compris', en: 'OK, got it', de: 'OK, verstanden', lb: 'OK, verstanen' },
};

const MaintenanceTab = ({ password }) => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setFeedback(null);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/maintenance`, {
        headers: { 'X-Admin-Password': password },
      });
      if (!r.ok) throw new Error(r.status);
      const d = await r.json();
      setData({
        ...DEFAULT_MAINT,
        ...d,
        message: { ...DEFAULT_MAINT.message, ...d.message },
        button:  { ...DEFAULT_MAINT.button,  ...d.button  },
      });
    } catch {
      setFeedback({ ok: false, msg: 'Chargement échoué' });
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setFeedback(null);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/maintenance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': password },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error(r.status);
      setFeedback({ ok: true, msg: 'Sauvegardé !' });
    } catch {
      setFeedback({ ok: false, msg: 'Sauvegarde échouée' });
    } finally {
      setSaving(false);
    }
  };

  const setEnabled = (v) => { setData(prev => ({ ...prev, enabled: v })); setFeedback(null); };
  const setMsg     = (lang, v) => { setData(prev => ({ ...prev, message: { ...prev.message, [lang]: v } })); setFeedback(null); };
  const setBtn     = (lang, v) => { setData(prev => ({ ...prev, button:  { ...prev.button,  [lang]: v } })); setFeedback(null); };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">

      {/* En-tête */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Maintenance</div>
          <h2 className="text-xl font-bold text-slate-900">Modale d'avertissement</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 disabled:opacity-50 transition"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Charger
          </button>
          <button
            onClick={save}
            disabled={saving || !data}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-ter-blue text-white text-sm font-bold hover:brightness-95 disabled:opacity-50 transition"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg mb-6 border ${
          feedback.ok
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {feedback.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {feedback.msg}
        </div>
      )}

      {/* Spinner initial */}
      {loading && !data && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {data && (
        <div className="space-y-6">

          {/* Toggle ON/OFF */}
          <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-4">Statut</div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {data.enabled ? '🟢 Modale active' : '⚫ Modale désactivée'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {data.enabled
                    ? "La modale s'affiche à l'ouverture du site."
                    : 'Aucune modale ne s\'affiche aux visiteurs.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={data.enabled}
                onClick={() => setEnabled(!data.enabled)}
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ter-blue focus:ring-offset-2 ${
                  data.enabled ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
              >
                <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition duration-200 ease-in-out ${
                  data.enabled ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>

          {/* Textes par langue */}
          <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-4">Textes par langue</div>
            <div className="space-y-4">
              {MAINT_LANGS.map(({ key, flag, label }) => (
                <div key={key} className="border border-slate-100 rounded-lg p-4">
                  <div className="text-xs font-bold text-slate-600 mb-3">{flag} {label}</div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Message affiché
                      </label>
                      <input
                        type="text"
                        value={data.message[key] ?? ''}
                        onChange={e => setMsg(key, e.target.value)}
                        className="w-full h-9 px-3 rounded-lg border border-slate-200 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Texte du bouton
                      </label>
                      <input
                        type="text"
                        value={data.button[key] ?? ''}
                        onChange={e => setBtn(key, e.target.value)}
                        className="w-full h-9 px-3 rounded-lg border border-slate-200 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

/* ============ Legal pages tab ============ */
const LEGAL_PAGES = [
  { key: 'mentions', label: 'Mentions légales' },
  { key: 'cgu',      label: 'CGU' },
  { key: 'privacy',  label: 'Politique de confidentialité' },
];
const LEGAL_LANGS = [
  { key: 'fr', label: 'Français' },
  { key: 'en', label: 'English' },
  { key: 'de', label: 'Deutsch' },
  { key: 'lb', label: 'Lëtzebuergesch' },
];

const LegalTab = ({ password }) => {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [activePage, setActivePage] = useState('mentions');
  const [activeLang, setActiveLang] = useState('fr');
  const [saving, setSaving]       = useState(false);
  const [feedback, setFeedback]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/legal`, { headers: { 'X-Admin-Password': password } });
      if (!r.ok) throw new Error(r.status);
      setData(await r.json());
    } catch { setFeedback({ ok: false, msg: 'Chargement échoué' }); }
    finally { setLoading(false); }
  }, [password]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setFeedback(null);
    try {
      const r = await fetch(`${API}/api/admin/infofluo/legal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': password },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error(r.status);
      setFeedback({ ok: true, msg: 'Sauvegardé !' });
    } catch { setFeedback({ ok: false, msg: 'Sauvegarde échouée' }); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => {
    setData(prev => ({
      ...prev,
      [activePage]: {
        ...prev[activePage],
        [activeLang]: { ...prev[activePage]?.[activeLang], [field]: value },
      },
    }));
    setFeedback(null);
  };

  const current = data?.[activePage]?.[activeLang];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Pages légales</div>
          <h2 className="text-xl font-bold text-slate-900">Gestion du contenu légal</h2>
        </div>
        <button
          onClick={save}
          disabled={saving || !data}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-ter-blue text-white text-sm font-bold hover:brightness-95 disabled:opacity-50 transition"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>

      {feedback && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg mb-6 border ${feedback.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {feedback.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {feedback.msg}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {LEGAL_PAGES.map(p => (
          <button
            key={p.key}
            onClick={() => setActivePage(p.key)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${activePage === p.key ? 'bg-ter-blue text-white' : 'bg-white border border-slate-200 text-slate-700 hover:border-ter-blue'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-6">
        {LEGAL_LANGS.map(l => (
          <button
            key={l.key}
            onClick={() => setActiveLang(l.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-bold border transition ${activeLang === l.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'}`}
          >
            {l.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {!loading && data && (
        <div className="space-y-5">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Titre (h1)</label>
            <input
              type="text"
              value={current?.title ?? ''}
              onChange={e => updateField('title', e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Contenu</label>
            <LegalEditor
              key={`${activePage}-${activeLang}`}
              content={current?.content ?? ''}
              onChange={v => updateField('content', v)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ============ Page entry ============ */
const Admin = () => {
  const { isAuthenticated, username, password, permissions, login, logout } = useAdminAuth();
  const [tab, setTab] = useState('ligne');

  // Redirect to first allowed tab when session loads or changes
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!permissions[tab]) {
      const first = TABS.find(t => permissions[t.key]);
      if (first) setTab(first.key);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) return <LoginScreen onLogin={login} />;
  return (
    <>
      <TabBar active={tab} onChange={setTab} onLogout={logout} permissions={permissions} />
      {tab === 'ligne'         && <AxisBoard password={password} />}
      {tab === 'quota'         && <QuotaTab password={password} />}
      {tab === 'communication' && <CommunicationTab password={password} />}
      {tab === 'infofluo'     && <InfoFluoTab password={password} />}
      {tab === 'legal'         && <LegalTab password={password} />}
      {tab === 'maintenance'   && <MaintenanceTab password={password} />}
      {tab === 'access'        && <AccessTab password={password} currentUsername={username} />}
      {tab === 'api'           && <ApiTab password={password} username={username} />}
    </>
  );
};

export default Admin;
